import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Validation regex patterns
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;
const ZIP_REGEX = /^\d{5}$/;

// Testimonials data
const TESTIMONIALS = [
  { text: 'Got $40k fast—Anonymous', stars: 5 },
  { text: 'Professional help, settled in weeks—J.M.', stars: 5 }
];

// Answer recap mapping
const getAnswerRecap = (answers) => {
  const recap = [];

  if (answers.injury === 'yes') {
    recap.push('You were injured in the accident');
  }

  if (answers.timeframe === '6mo') {
    recap.push('Accident occurred within the last 6 months');
  } else if (answers.timeframe === '1yr') {
    recap.push('Accident occurred within the last year');
  }

  if (answers.fault === 'other') {
    recap.push('Other driver was at fault');
  } else if (answers.fault === 'shared') {
    recap.push('Fault was shared');
  } else if (answers.fault === 'unsure') {
    recap.push('Fault is being determined');
  }

  if (answers.medical === 'hospital') {
    recap.push('You received hospital treatment');
  } else if (answers.medical === 'outpatient') {
    recap.push('You received medical care');
  }

  if (answers.ongoing === 'significant' || answers.ongoing === 'some') {
    recap.push('You are still experiencing pain or discomfort');
  }

  return recap.slice(0, 5); // Max 5 bullets
};

// Format phone number as user types (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

function Form() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const firstInputRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    zip: '',
    description: '',
    consent: false
  });
  const [errors, setErrors] = useState({});
  const [validFields, setValidFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState(null);
  const [qualityScore, setQualityScore] = useState(0);
  const [recapExpanded, setRecapExpanded] = useState(false);

  useEffect(() => {
    // Retrieve quiz data from sessionStorage
    const answersStr = sessionStorage.getItem('quizAnswers');
    const scoreStr = sessionStorage.getItem('quizScore');
    const completed = sessionStorage.getItem('quizCompleted');

    if (!completed || !answersStr) {
      // Redirect to quiz if not completed
      navigate('/');
      return;
    }

    try {
      const answers = JSON.parse(answersStr);
      const score = parseFloat(scoreStr) || 0;
      setQuizAnswers(answers);
      setQualityScore(score);
    } catch (error) {
      console.error('Error parsing quiz data:', error);
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    // Auto-scroll to form on mount (smooth)
    if (formRef.current) {
      setTimeout(() => {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [quizAnswers]);

  useEffect(() => {
    // Auto-focus first field
    if (firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current.focus();
      }, 500);
    }
  }, [quizAnswers]);

  useEffect(() => {
    // Load TrustedForm certification script
    if (typeof window !== 'undefined') {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = ("https:" === document.location.protocol ? 'https' : 'http') +
        '://api.trustedform.com/trustedform.js?field=xxTrustedFormCertUrl&use_tagged_consent=true&l=' +
        new Date().getTime() + Math.random();

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }

      // Cleanup function
      return () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, []);

  const validateField = (name, value) => {
    let isValid = false;

    switch (name) {
      case 'firstName':
      case 'lastName':
        isValid = value.trim().length > 0;
        break;
      case 'phone':
        const phoneDigits = value.replace(/\D/g, '');
        isValid = PHONE_REGEX.test(phoneDigits);
        break;
      case 'email':
        isValid = EMAIL_REGEX.test(value);
        break;
      case 'zip':
        isValid = ZIP_REGEX.test(value);
        break;
      case 'consent':
        isValid = value === true;
        break;
      default:
        isValid = false;
    }

    return isValid;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }

    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
      setValidFields({ ...validFields, [name]: validateField(name, checked) });
    } else if (name === 'description') {
      // Limit description to 200 characters
      if (value.length <= 200) {
        setFormData({ ...formData, [name]: value });
      }
    } else if (name === 'phone') {
      // Format phone as (XXX) XXX-XXXX
      const formatted = formatPhoneNumber(value);
      setFormData({ ...formData, [name]: formatted });
      setValidFields({ ...validFields, [name]: validateField(name, formatted) });
    } else if (name === 'zip') {
      // Only allow digits, max 5
      const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
      setFormData({ ...formData, [name]: digitsOnly });
      setValidFields({ ...validFields, [name]: validateField(name, digitsOnly) });
    } else {
      setFormData({ ...formData, [name]: value });
      setValidFields({ ...validFields, [name]: validateField(name, value) });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Phone validation (10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits) {
      newErrors.phone = 'Phone number is required';
    } else if (!PHONE_REGEX.test(phoneDigits)) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // ZIP validation (5 digits)
    if (!formData.zip.trim()) {
      newErrors.zip = 'ZIP code is required';
    } else if (!ZIP_REGEX.test(formData.zip)) {
      newErrors.zip = 'ZIP must be 5 digits';
    }

    // Consent validation
    if (!formData.consent) {
      newErrors.consent = 'You must agree to be contacted';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = document.querySelector('.form-input.error, .form-textarea.error');
      if (firstErrorField) {
        firstErrorField.focus();
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Capture TrustedForm certificate URL from hidden field
      const trustedFormField = document.querySelector('input[name="xxTrustedFormCertUrl"]');
      const trustedFormCertUrl = trustedFormField ? trustedFormField.value : null;

      // Prepare lead data for Firestore
      const leadData = {
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.replace(/\D/g, ''), // Store only digits
        email: formData.email.trim().toLowerCase(),
        zip: formData.zip.trim(),
        description: formData.description.trim() || null,
        answers: quizAnswers,
        qualityScore: qualityScore,
        consent: formData.consent,
        trustedFormCertUrl: trustedFormCertUrl, // TrustedForm certificate URL
        timestamp: serverTimestamp(),
        status: 'new',
        source: 'web-form'
      };

      // Add document to Firestore 'leads' collection
      const docRef = await addDoc(collection(db, 'leads'), leadData);
      console.log('Lead submitted with ID:', docRef.id);

      // Store lead ID in sessionStorage for confirmation page
      sessionStorage.setItem('leadId', docRef.id);

      // Navigate to CTA/confirmation page
      navigate('/cta');
    } catch (error) {
      console.error('Error submitting lead:', error);
      setErrors({ submit: 'An error occurred. Please try again or call us at (844) 522-1358.' });
      setIsSubmitting(false);
    }
  };

  if (!quizAnswers) {
    return null; // Will redirect in useEffect
  }

  const answerRecap = getAnswerRecap(quizAnswers);
  const formProgress = Object.keys(validFields).filter(k => validFields[k]).length;
  const totalRequiredFields = 5; // firstName, lastName, phone, email, zip

  return (
    <div className="container">
      {/* Qualification Header */}
      <div className="form-header text-center">
        <h1 className="form-headline">
          <span style={{marginRight: '8px'}}>✓</span>
          You May Qualify!
        </h1>
        <p className="form-subheadline">Based on your answers, you could be eligible for compensation</p>
      </div>

      {/* Urgency Header with Benefits */}
      <div className="urgency-banner">
        <h2 className="urgency-title">Complete Now for Free Consultation in Under 2 Hours!</h2>
        <ul className="urgency-benefits">
          <li><span className="benefit-icon">⚡</span> Instant specialist matching</li>
          <li><span className="benefit-icon">💰</span> Get personalized payout estimate</li>
          <li><span className="benefit-icon">📞</span> Priority callback within 2 hours</li>
        </ul>
      </div>

      {/* Contact Form - ABOVE THE FOLD */}
      <div ref={formRef} className="card form-container form-priority">
        {/* TrustedForm noscript fallback */}
        <noscript>
          <img src='https://api.trustedform.com/ns.gif' alt="" style={{display: 'none'}} />
        </noscript>

        <h2 className="form-title">Get Your Free Consultation</h2>

        {/* Form Progress Indicator */}
        {formProgress > 0 && (
          <div className="form-progress-indicator">
            <div className="progress-text-mini">
              Form Progress: {formProgress} of {totalRequiredFields} required fields completed
            </div>
            <div className="progress-bar" style={{height: '6px', marginBottom: '16px'}}>
              <div
                className="progress-fill"
                style={{width: `${(formProgress / totalRequiredFields) * 100}%`}}
              ></div>
            </div>
          </div>
        )}

        {errors.submit && (
          <div className="alert alert-error" role="alert">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Critical Form Header */}
          <div className="critical-form-header">
            Provide Your Details Below—Critical for Your Free Consultation
          </div>

          {/* First Name - Emphasized with Real-time Validation */}
          <div className="form-group">
            <label htmlFor="firstName" className="form-label-emphasized">
              First Name <span className="required-asterisk">*</span>
              {validFields.firstName && <span className="validation-check">✔</span>}
            </label>
            <span className="label-subtext">Required for personalized contact</span>
            <input
              ref={firstInputRef}
              type="text"
              id="firstName"
              name="firstName"
              className={`form-input ${errors.firstName ? 'error' : ''} ${validFields.firstName ? 'valid' : ''}`}
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter your first name"
              aria-required="true"
              aria-invalid={!!errors.firstName}
              aria-describedby={errors.firstName ? 'firstName-error firstName-help' : 'firstName-help'}
            />
            {errors.firstName && (
              <span id="firstName-error" className="error-message" role="alert">
                <span className="icon-warning"></span>{errors.firstName}
              </span>
            )}
          </div>

          {/* Last Name - Emphasized with Real-time Validation */}
          <div className="form-group">
            <label htmlFor="lastName" className="form-label-emphasized">
              Last Name <span className="required-asterisk">*</span>
              {validFields.lastName && <span className="validation-check">✔</span>}
            </label>
            <span className="label-subtext">Required for personalized contact</span>
            <input
              type="text"
              id="lastName"
              name="lastName"
              className={`form-input ${errors.lastName ? 'error' : ''} ${validFields.lastName ? 'valid' : ''}`}
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter your last name"
              aria-required="true"
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? 'lastName-error lastName-help' : 'lastName-help'}
            />
            {errors.lastName && (
              <span id="lastName-error" className="error-message" role="alert">
                <span className="icon-warning"></span>{errors.lastName}
              </span>
            )}
          </div>

          {/* Phone - Emphasized with Real-time Validation and Formatting */}
          <div className="form-group">
            <label htmlFor="phone" className="form-label-emphasized">
              Phone Number <span className="required-asterisk">*</span>
              {validFields.phone && <span className="validation-check">✔</span>}
            </label>
            <span className="label-subtext">We'll call you ASAP—US format only (10 digits)</span>
            <input
              type="tel"
              id="phone"
              name="phone"
              className={`form-input ${errors.phone ? 'error' : ''} ${validFields.phone ? 'valid' : ''}`}
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
              aria-required="true"
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? 'phone-error phone-help' : 'phone-help'}
            />
            {errors.phone && (
              <span id="phone-error" className="error-message" role="alert">
                <span className="icon-warning"></span>{errors.phone}
              </span>
            )}
          </div>

          {/* Email - Emphasized with Real-time Validation */}
          <div className="form-group">
            <label htmlFor="email" className="form-label-emphasized">
              Email Address <span className="required-asterisk">*</span>
              {validFields.email && <span className="validation-check">✔</span>}
            </label>
            <span className="label-subtext">Required for confirmation and case updates</span>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${errors.email ? 'error' : ''} ${validFields.email ? 'valid' : ''}`}
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              aria-required="true"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error email-help' : 'email-help'}
            />
            {errors.email && (
              <span id="email-error" className="error-message" role="alert">
                <span className="icon-warning"></span>{errors.email}
              </span>
            )}
          </div>

          {/* ZIP Code - Emphasized with Real-time Validation */}
          <div className="form-group">
            <label htmlFor="zip" className="form-label-emphasized">
              ZIP Code <span className="required-asterisk">*</span>
              {validFields.zip && <span className="validation-check">✔</span>}
            </label>
            <span className="label-subtext">Match you with local legal experts—US format (5 digits)</span>
            <input
              type="text"
              id="zip"
              name="zip"
              className={`form-input ${errors.zip ? 'error' : ''} ${validFields.zip ? 'valid' : ''}`}
              value={formData.zip}
              onChange={handleChange}
              placeholder="12345"
              maxLength="5"
              inputMode="numeric"
              aria-required="true"
              aria-invalid={!!errors.zip}
              aria-describedby={errors.zip ? 'zip-error zip-help' : 'zip-help'}
            />
            {errors.zip && (
              <span id="zip-error" className="error-message" role="alert">
                <span className="icon-warning"></span>{errors.zip}
              </span>
            )}
          </div>

          {/* Description (Optional) - Enhanced with Better Prompt */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Additional Details (Optional)
            </label>
            <span className="label-subtext">Share details to prioritize your case and boost your payout estimate</span>
            <textarea
              id="description"
              name="description"
              className="form-textarea"
              value={formData.description}
              onChange={handleChange}
              placeholder="Share injury type, police report status, fault details, or medical treatment—helps boost your case score! (200 char max)"
              maxLength="200"
              rows="6"
              aria-describedby="description-counter"
            />
            <span id="description-counter" className="character-counter">
              {formData.description.length}/200 characters
            </span>
          </div>

          {/* TCPA Consent */}
          <div className="form-group consent-group">
            <label className="consent-label">
              <input
                type="checkbox"
                name="consent"
                className="consent-checkbox"
                checked={formData.consent}
                onChange={handleChange}
                aria-required="true"
                aria-invalid={!!errors.consent}
                aria-describedby={errors.consent ? 'consent-error' : undefined}
              />
              <span className="consent-text">
                I agree to be contacted by ClaimYourCrash.com and its partners via phone, email, or text (including automated communications) at the number and email provided. I understand consent is not required to use this service. Message and data rates may apply.
                <Link to="/privacy" target="_blank" className="consent-link"> Privacy Policy</Link>.
              </span>
            </label>
            {errors.consent && (
              <span id="consent-error" className="error-message" role="alert">
                <span className="icon-warning"></span>{errors.consent}
              </span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn btn-success btn-submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Get My Free Consultation Now'}
          </button>

          <p className="form-disclaimer text-center">
            By submitting, you agree to our <Link to="/terms">Terms of Service</Link>. No attorney-client relationship is created. This is not legal advice.
          </p>
        </form>
      </div>

      {/* Collapsible Case Summary - BELOW FORM */}
      <div className="card recap-card-collapsible">
        <button
          className="collapsible-toggle"
          onClick={() => setRecapExpanded(!recapExpanded)}
          aria-expanded={recapExpanded}
        >
          <h3 className="recap-title-collapsible">
            {recapExpanded ? '▼' : '▶'} Your Case Summary (Quality Score: {qualityScore.toFixed(1)}/10)
          </h3>
        </button>

        {recapExpanded && (
          <div className="collapsible-content">
            <ul className="recap-list">
              {answerRecap.map((item, index) => (
                <li key={index} className="recap-item">
                  <span className="recap-icon">✓</span> {item}
                </li>
              ))}
            </ul>
            <div className="recap-score">
              <div className="progress-bar" style={{height: '8px', maxWidth: '200px', margin: '0 auto'}}>
                <div
                  className="progress-fill"
                  style={{width: `${qualityScore * 10}%`}}
                  aria-label={`Quality score: ${qualityScore} out of 10`}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compact Testimonials - Below Form */}
      <div className="testimonials-compact">
        {TESTIMONIALS.map((testimonial, index) => (
          <div key={index} className="testimonial-card-compact">
            <div className="testimonial-stars" aria-label={`${testimonial.stars} stars`}>
              {'★'.repeat(testimonial.stars)}
            </div>
            <p className="testimonial-text-compact">"{testimonial.text}"</p>
          </div>
        ))}
      </div>

      {/* Trust Indicators */}
      <div className="form-trust text-center">
        <p className="trust-text">
          <span className="trust-icon">🔒</span> SSL Encrypted | Your data is secure
        </p>
        <p className="trust-text">
          <span className="trust-icon">⚡</span> Fast Response | Average reply under 2 hours
        </p>
      </div>
    </div>
  );
}

export default Form;
